// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LazyImageProps } from "@sc/MobiusOverrides/LazyImage";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import React from "react";

interface TextWithIconsProps extends WidgetProps {
    fields: {
        title: string;
        cardWidth: "100%" | "50%" | "33%" | "25%";
        tiles: {
            fields: {
                icon: string;
                heading: string;
                url: LinkFieldValue;
            };
        }[];
    };
}
export interface TextWithIconsStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    tilesContainer?: InjectableCss;
    tileWrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    icon?: LazyImageProps;
    link?: ClickableProps;
}

export const textWithIconsStyles: TextWithIconsStyles = {};

const TextWithIconsWidget: React.FC<TextWithIconsProps> = ({ id, fields }) => {
    const styles = textWithIconsStyles;
    const links = useGetLinks(fields.tiles, o => o.fields.url);
    const width = fields.cardWidth;
    return (
        <StyledWrapper {...styles.container}>
            {fields.title && <Typography {...styles.title}>{fields.title}</Typography>}
            <StyledWrapper {...styles.tilesContainer}>
                {fields.tiles?.length &&
                    fields.tiles.map((item, index) => {
                        const { icon, heading, url } = item.fields;
                        return url?.value ? (
                            <Clickable aria-label={heading} href={links[index].url} key={`StyledWrapper-${id}-${index}`} {...styles.link} {...{width}}>
                                <LazyImage src={icon} {...styles.icon}></LazyImage>
                                <Typography {...styles.heading}>{heading}</Typography>
                            </Clickable>
                        ) : (
                            <StyledWrapper key={`StyledWrapper-${id}-${index}`} {...styles.tileWrapper} {...{width}}>
                                <LazyImage src={icon} {...styles.icon}></LazyImage>
                                <Typography {...styles.heading}>{heading}</Typography>
                            </StyledWrapper>
                        );
                    })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export const definition: WidgetDefinition = {
    group: XNGAGE_CUSTOM_WIDGET,
    fieldDefinitions: [
        {
            name: "title",
            displayName: "title",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: false,
        },
        {
            name: "cardWidth",
            displayName: "card width",
            editorTemplate: "DropDownField",
            fieldType: "General",
            options: [
                { displayName: "100%", value: "100%" },
                { displayName: "50%", value: "50%" },
                { displayName: "33%", value: "33%" },
                { displayName: "25%", value: "25%" },
            ],
            defaultValue: "25%",
            hideEmptyOption: true,
        },
        {
            name: "tiles",
            editorTemplate: "ListField",
            getDisplay: ({ fields: { heading } }) => heading || "Tile",
            defaultValue: [],
            fieldType: "General",
            fieldDefinitions: [
                {
                    name: "heading",
                    displayName: "Heading",
                    editorTemplate: "TextField",
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: "icon",
                    displayName: "Icon",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                },
                {
                    name: "url",
                    displayName: "Link",
                    isRequired: false,
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Page",
                    },
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: TextWithIconsWidget,
    definition,
};

export default widgetModule;
